import { MutualFundNavEntry } from '@/types/mutualFund';

export { computeMutualFundMetrics } from '@/types/mutualFund';

export type NavPeriod = 'YTD' | '1M' | '3M' | '6M' | '1Y' | '3Y' | '5Y' | 'ALL';

/**
 * Returns the ordinal suffix for a given day number.
 */
function ordinalSuffix(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Formats an ISO date string as "NAV dated as of {D}{suffix} {Month} {YYYY}".
 * e.g. "NAV dated as of 13th March 2026"
 */
export function formatNavDate(isoDate: string): string {
  const date = new Date(isoDate);
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  return `NAV dated as of ${day}${ordinalSuffix(day)} ${month} ${year}`;
}

/**
 * Computes the start date for a given NavPeriod relative to today.
 * Returns null for 'ALL' (no filter).
 */
function getStartDate(period: NavPeriod, today: Date): Date | null {
  const d = new Date(today);
  switch (period) {
    case 'YTD':
      return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    case '1M':
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, d.getUTCDate()));
    case '3M':
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 3, d.getUTCDate()));
    case '6M':
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 6, d.getUTCDate()));
    case '1Y':
      return new Date(Date.UTC(d.getUTCFullYear() - 1, d.getUTCMonth(), d.getUTCDate()));
    case '3Y':
      return new Date(Date.UTC(d.getUTCFullYear() - 3, d.getUTCMonth(), d.getUTCDate()));
    case '5Y':
      return new Date(Date.UTC(d.getUTCFullYear() - 5, d.getUTCMonth(), d.getUTCDate()));
    case 'ALL':
      return null;
  }
}

/**
 * Filters NAV history entries to those within the given period relative to today.
 * The `today` parameter is injectable for deterministic testing.
 */
export function filterNavByPeriod(
  entries: MutualFundNavEntry[],
  period: NavPeriod,
  today: Date = new Date()
): MutualFundNavEntry[] {
  const startDate = getStartDate(period, today);
  if (startDate === null) return entries;
  const startMs = startDate.getTime();
  return entries.filter((e) => new Date(e.nav_date).getTime() >= startMs);
}
